import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { usePlus } from '../../context/PlusContext';
import { usePerfil } from '../../context/PerfilContext';
import { formatoEuros } from '../../utils/numeros';

/** Vista de los informes: un mes (mes 1–12) o un año entero (mes null). */
export interface VistaInforme {
  anio: number;
  mes: number | null;
}

/**
 * Colores de las gráficas. Ingresos en fiordo y gastos en baya: se distinguen
 * también con daltonismo (validado con la herramienta de dataviz). Las líneas
 * y los textos van en tinta, nunca en el color de la serie.
 */
export function useColoresGrafico() {
  const { darkMode } = useTheme();
  return darkMode
    ? { ingresos: '#6584A0', gastos: '#C8584A', tinta: '#DDD8CF', suave: '#9AA0A6', rejilla: '#2F363C', fondo: '#20262B' }
    : { ingresos: '#6584A0', gastos: '#C8584A', tinta: '#2F363C', suave: '#646A71', rejilla: '#ECE9E3', fondo: '#FFFFFF' };
}

export const Tarjeta: React.FC<{
  titulo: string;
  subtitulo?: string;
  accion?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ titulo, subtitulo, accion, children, className = '' }) => (
  <section className={`bg-white dark:bg-gray-800 rounded-xl shadow p-4 md:p-5 ${className}`}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h2 className="font-bold text-gray-900 dark:text-white">{titulo}</h2>
        {subtitulo && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitulo}</p>}
      </div>
      {accion}
    </div>
    {children}
  </section>
);

/**
 * Etiqueta pequeña de «Plus» junto al título de un informe de Plus. Mientras no
 * haya límites (todo gratis) no sale: no se anuncia algo que no se puede comprar.
 */
export const EtiquetaPlus: React.FC = () => {
  const { estadoPlus } = usePerfil();
  if (estadoPlus.motivo === 'sin-limites') return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 shrink-0">
      <Sparkles className="w-3 h-3" /> Plus
    </span>
  );
};

/**
 * Lo que ve quien no tiene Plus en lugar de un informe de Plus: qué enseña y
 * un botón que abre la hoja de Plus. Nunca un error.
 */
export const BloquePlus: React.FC<{ titulo: string; texto: string }> = ({ titulo, texto }) => {
  const { ofrecerPlus } = usePlus();
  return (
    <Tarjeta titulo={titulo} accion={<EtiquetaPlus />}>
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-gray-600 dark:text-gray-300">{texto}</p>
          <button
            type="button"
            onClick={() => ofrecerPlus('informesPlus')}
            className="mt-3 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
          >
            Ver myarinn Plus
          </button>
        </div>
      </div>
    </Tarjeta>
  );
};

/** Tooltip de las gráficas de recharts, con importes en euros. */
export const TooltipEuros: React.FC<{
  active?: boolean;
  payload?: { name?: string; value?: number; color?: string; dataKey?: string }[];
  label?: string;
  titulo?: (label: string) => string;
}> = ({ active, payload, label, titulo }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow px-3 py-2 text-xs">
      <p className="font-semibold text-gray-900 dark:text-white mb-1 capitalize">{titulo ? titulo(String(label)) : label}</p>
      {payload.map(p => (
        <p key={p.dataKey ?? p.name} className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
          <span>{p.name}</span>
          <span className="ml-auto font-semibold text-gray-900 dark:text-white tabular-nums">{formatoEuros(Number(p.value ?? 0))}</span>
        </p>
      ))}
    </div>
  );
};

/** Leyenda encima de una gráfica: un cuadrado (o raya) del color y el nombre. */
export const Leyenda: React.FC<{ series: { nombre: string; color: string; linea?: boolean }[] }> = ({ series }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-xs text-gray-600 dark:text-gray-300">
    {series.map(s => (
      <span key={s.nombre} className="flex items-center gap-1.5">
        {s.linea
          ? <span className="w-4 h-0.5 rounded" style={{ backgroundColor: s.color }} />
          : <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />}
        {s.nombre}
      </span>
    ))}
  </div>
);

/** Mes para las tablas: «nov 2025». Corto para que quepa en el ancho de un iPhone. */
export const mesCorto = (anio: number, mes: number) =>
  new Date(anio, mes - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' }).replace('.', '');

/**
 * Los datos de una gráfica en una tabla, plegados: para leerlos exactos o sin ver la gráfica.
 * Las celdas no se parten (en iPhone el «€» bajaba a otra línea y las cabeceras se juntaban):
 * si no cabe, la tabla se desliza de lado con la primera columna fija.
 */
export const TablaDatos: React.FC<{ cabecera: string[]; filas: (string | number)[][] }> = ({ cabecera, filas }) => (
  <details className="mt-2">
    <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">Ver los datos</summary>
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-xs whitespace-nowrap">
        <thead>
          <tr className="text-gray-500 dark:text-gray-400">
            {cabecera.map((c, i) => (
              <th key={c} className={`py-1 font-semibold ${i ? 'text-right pl-3' : 'text-left pr-1 sticky left-0 bg-white dark:bg-gray-800'}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {filas.map(f => (
            <tr key={String(f[0])} className="text-gray-700 dark:text-gray-200">
              {f.map((v, i) => (
                <td key={i} className={`py-1 tabular-nums ${i ? 'text-right pl-3' : 'capitalize pr-1 sticky left-0 bg-white dark:bg-gray-800'}`}>{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
);

/** Formato corto para el eje de las gráficas: «1,2 mil €». */
export const ejeEuros = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1000) return `${(v / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })} mil`;
  return `${Math.round(v)}`;
};

export const Vacio: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">{children}</p>
);
